import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    MdClose, MdBusiness, MdMenu, MdDownload,
    MdChecklist, MdEventAvailable, MdBuild, MdInsights,
    MdInventory2, MdLocalShipping, MdReportProblem
} from 'react-icons/md';
import {
    FaFacebookF, FaInstagram, FaLinkedinIn, FaTiktok, FaXTwitter, FaYoutube
} from 'react-icons/fa6';
import { LanguageSelector, useLanguage } from '../i18n';
import { API_BASE, withSystem, localizedField, SYSTEM_SLUG } from './publicApi';

const SOCIAL_LINKS = [
    { key: 'instagram', label: 'Instagram', icon: <FaInstagram /> },
    { key: 'x', label: 'X', icon: <FaXTwitter /> },
    { key: 'tiktok', label: 'TikTok', icon: <FaTiktok /> },
    { key: 'linkedin', label: 'LinkedIn', icon: <FaLinkedinIn /> },
    { key: 'facebook', label: 'Facebook', icon: <FaFacebookF /> },
    { key: 'youtube', label: 'YouTube', icon: <FaYoutube /> },
];

const getSocialUrl = (homepage, key) => homepage?.social?.[key]
    || homepage?.[`social_${key}`]
    || homepage?.[`homepage_social_${key}`]
    || '';

// ── Main Public Page ──────────────────────────────────────────────────────────
// Kept deliberately short (Navbar → Hero → Partners → Footer) so it reads as
// a two-screen scroll. Everything that used to live here as its own section
// — About, Services, Space Booking, Report a Concern, Contact, Privacy
// Policy / Terms — now has its own route (see App.js) and is one nav-bar or
// footer click away instead of adding to this page's length.
export default function PublicBooking() {
    const { language, t } = useLanguage();
    // An organization's own public page shows only what that organization wrote:
    // the platform's built-in marketing wording is hidden.
    const ownSystem = !!SYSTEM_SLUG;
    const [mobileMenu, setMobileMenu] = useState(false);
    const [homepage, setHomepage] = useState(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [installPrompt, setInstallPrompt] = useState(null);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);

    useEffect(() => {
        axios.get(`${API_BASE}/homepage`, { params: withSystem() }).then(response => {
            setHomepage(response.data);
        }).catch(() => { });
    }, []);

    useEffect(() => {
        if (!homepage?.slides?.length || homepage.slides.length < 2) return undefined;
        const timer = setInterval(() => setSlideIndex(index => (index + 1) % homepage.slides.length), 6000);
        return () => clearInterval(timer);
    }, [homepage]);

    useEffect(() => {
        const installed = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        const dismissedUntil = Number(localStorage.getItem('miti_fms_install_prompt_dismissed_until') || 0);
        if (installed || Date.now() < dismissedUntil) return undefined;

        const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
        if (isIos) setShowInstallPrompt(true);

        const handleBeforeInstallPrompt = event => {
            event.preventDefault();
            setInstallPrompt(event);
            setShowInstallPrompt(true);
        };
        const handleAppInstalled = () => {
            setInstallPrompt(null);
            setShowInstallPrompt(false);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const isLoggedIn = !!localStorage.getItem('fms_token');

    const dismissInstallPrompt = () => {
        localStorage.setItem('miti_fms_install_prompt_dismissed_until', String(Date.now() + 7 * 24 * 60 * 60 * 1000));
        setShowInstallPrompt(false);
    };

    const installApp = async () => {
        if (!installPrompt) return;
        await installPrompt.prompt();
        setInstallPrompt(null);
        setShowInstallPrompt(false);
    };

    const currentSlide = homepage?.slides?.[slideIndex];
    const currentSlideTitle = currentSlide ? localizedField(currentSlide, 'title', language) : '';
    const heroTitle = localizedField(homepage, 'hero_title', language);
    const heroSubtitle = currentSlideTitle || localizedField(homepage, 'hero_subtitle', language);
    const heroDescription = localizedField(homepage, 'hero_description', language);
    const heroStyle = (currentSlide?.image && !currentSlide?.video)
        ? { backgroundImage: `linear-gradient(100deg, rgba(5,10,24,.88), rgba(8,145,178,.55)), url(${currentSlide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: 'linear-gradient(160deg,#050a18 0%,#123a7a 55%,#0891b2 100%)' };

    const aboutText = localizedField(homepage, 'about', language);
    const hideLink = href => ownSystem && (href === '/services' || (href === '/about' && !aboutText));
    const navLinks = [['#home', t('publicBooking.navHome', 'Home')], ['/about', t('publicBooking.navAbout', 'About')], ['/services', t('publicBooking.navServices', 'Services')], ['/booking', t('publicBooking.navBookSpace', 'Book a Space')], ['/report', t('publicBooking.navReport', 'Report an Issue')], ['/contact', t('publicBooking.navContact', 'Contact')]].filter(([href]) => !hideLink(href));

    return (
        <div className="public-booking-page" style={{ minHeight: '100vh', background: '#050a18', fontFamily: "'Inter',-apple-system,sans-serif" }}>

            {/* ── Navbar ── */}
            <nav className="public-booking-nav" style={{
                background: 'rgba(5,10,24,0.92)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(103,232,249,0.15)', padding: '0 5%', height: 72,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                position: 'sticky', top: 0, zIndex: 200, boxShadow: '0 1px 20px rgba(0,0,0,0.25)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={homepage?.logo_url || '/logo512.png'} alt={`${homepage?.system_name || 'MITI FMS'} logo`} style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover' }} />
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 17, color: '#fff', lineHeight: 1 }}>{homepage?.system_name || 'MITI FMS'}</div>
                        {!ownSystem && <div style={{ fontSize: 10, color: '#64748b', letterSpacing: '1px' }}>FACILITY MANAGEMENT</div>}
                    </div>
                </div>

                {/* Desktop nav */}
                <div style={{ display: 'flex', gap: 30, alignItems: 'center' }} className="d-none d-md-flex">
                    {navLinks.map(([href, label]) => (
                        <a key={href} href={href} style={{ fontSize: 14, fontWeight: 500, color: '#cbd5e1', textDecoration: 'none', transition: 'color 0.2s' }}
                            onMouseEnter={e => e.target.style.color = '#67e8f9'} onMouseLeave={e => e.target.style.color = '#cbd5e1'}>
                            {label}
                        </a>
                    ))}
                </div>

                <div className="public-booking-actions" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <LanguageSelector style={{
                        background: 'transparent',
                        borderRadius: 999,
                    }} />
                    {isLoggedIn ? (
                        <a href="/app/dashboard" style={{ fontSize: 12.5, color: '#fff', fontWeight: 700, textDecoration: 'none', background: '#2563eb', padding: '7px 16px', borderRadius: 999 }}>
                            {t('publicBooking.dashboardLink', 'Dashboard →')}
                        </a>
                    ) : (
                        <a href="/login" style={{ fontSize: 12.5, color: '#fff', fontWeight: 600, textDecoration: 'none', border: '1.5px solid rgba(103,232,249,0.35)', padding: '6px 15px', borderRadius: 999 }}>
                            {t('publicBooking.loginLink', 'Login')}
                        </a>
                    )}
                    <button className="d-md-none" onClick={() => setMobileMenu(!mobileMenu)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                        <MdMenu size={24} style={{ color: '#cbd5e1' }} />
                    </button>
                </div>
            </nav>

            {/* Mobile menu */}
            {mobileMenu && (
                <div style={{ background: '#0a1330', borderBottom: '1px solid rgba(103,232,249,0.15)', padding: '8px 20px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.35)' }}>
                    {navLinks.map(([href, label]) => (
                        <a key={href} href={href} onClick={() => setMobileMenu(false)}
                            style={{ display: 'block', padding: '10px 0', fontSize: 15, fontWeight: 500, color: '#cbd5e1', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            {label}
                        </a>
                    ))}
                </div>
            )}

            {showInstallPrompt && (
                <aside className="install-app-prompt" aria-label={t('publicBooking.installAppLabel', 'Install {app}', { app: homepage?.system_name || 'MITI FMS' })}>
                    <button className="install-app-dismiss" type="button" onClick={dismissInstallPrompt} aria-label={t('publicBooking.dismissInstallAria', 'Dismiss install prompt')}><MdClose size={20} /></button>
                    <div className="install-app-icon"><MdBusiness size={22} /></div>
                    <div className="install-app-copy">
                        <strong>{t('publicBooking.installAppLabel', 'Install {app}', { app: homepage?.system_name || 'MITI FMS' })}</strong>
                        <span>{installPrompt ? t('publicBooking.installHintAndroid', 'Add fast access from your home screen.') : t('publicBooking.installHintIos', 'Tap Share, then Add to Home Screen.')}</span>
                    </div>
                    {installPrompt && <button className="install-app-action" type="button" onClick={installApp}><MdDownload size={18} /> {t('publicBooking.installButton', 'Install')}</button>}
                </aside>
            )}

            {/* ── Hero ── */}
            {/* minHeight targets the hero video's own 16:9 shape via 56vw at
                every viewport width, so object-fit:cover on the <video>
                below rarely has much to crop. The floor matters here: an
                earlier version used a flat 440px floor meant for desktop,
                but on a narrow phone 56vw is well under 440, so that floor
                won and forced a tall, near-square box against a landscape
                video -- cropping most of the left/right sides away. 200px is
                below the section's own padding (96+112=208px), so on phones
                the padding itself sets the height (roughly matching 16:9
                already) instead of an oversized floor fighting it; the vw
                term still takes over and matches 16:9 closely as screens
                widen. Being a floor (not a fixed height), it never forces
                the box shorter than hero text actually needs, so nothing
                gets clipped if that text is filled in later. Capped so it
                doesn't balloon on ultra-wide monitors. display:flex +
                justifyContent:center keeps text vertically centered. */}
            <section id="home" className="public-booking-hero" style={{ ...heroStyle, position: 'relative', overflow: 'hidden', padding: '96px 5% 112px', minHeight: 'clamp(200px, 56vw, 760px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', transition: 'background-image 0.5s ease' }}>
                {currentSlide?.video && <video key={currentSlide.video} autoPlay muted loop playsInline preload="auto" poster={currentSlide.image || undefined}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
                    <source src={currentSlide.video} />
                </video>}
                {currentSlide?.video && <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(100deg, rgba(5,10,24,.82), rgba(8,145,178,.5))', zIndex: 0 }} />}
                <div aria-hidden="true" style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.5,
                    backgroundImage: 'radial-gradient(rgba(148,197,255,0.3) 1px, transparent 1px)',
                    backgroundSize: '34px 34px',
                }} />

                {/* ── Floating feature objects — a quiet, moving glimpse of the system ── */}
                <style>{`
                    @keyframes floatA { 0%, 100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(8px,-20px) rotate(6deg); } }
                    @keyframes floatB { 0%, 100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(-10px,-14px) rotate(-5deg); } }
                    @keyframes floatC { 0%, 100% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(6px,16px) rotate(4deg); } }
                    .hero-float { position: absolute; pointer-events: none; z-index: 0; }
                    .hero-float-badge {
                        width: 100%; height: 100%; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                        background: radial-gradient(circle at 35% 30%, rgba(56,189,248,0.22), rgba(10,19,48,0.75));
                        border: 1px solid rgba(103,232,249,0.32); color: #67e8f9;
                        box-shadow: 0 0 18px 2px rgba(56,189,248,0.28), inset 0 0 10px rgba(56,189,248,0.12);
                        backdrop-filter: blur(2px);
                    }
                    @media (max-width: 767px) { .hero-float { display: none; } }
                `}</style>
                {[
                    { icon: <MdChecklist size={20} />, top: '16%', left: '6%', size: 56, anim: 'floatA', dur: '6.5s', delay: '0s' },
                    { icon: <MdEventAvailable size={20} />, top: '20%', right: '7%', size: 60, anim: 'floatB', dur: '7.5s', delay: '0.6s' },
                    { icon: <MdBuild size={18} />, top: '60%', left: '4%', size: 48, anim: 'floatC', dur: '5.5s', delay: '1.1s' },
                    { icon: <MdInsights size={18} />, top: '64%', right: '5%', size: 50, anim: 'floatA', dur: '7s', delay: '1.6s' },
                    { icon: <MdInventory2 size={16} />, top: '8%', right: '22%', size: 42, anim: 'floatC', dur: '6s', delay: '0.3s' },
                    { icon: <MdLocalShipping size={16} />, top: '76%', left: '20%', size: 44, anim: 'floatB', dur: '6.8s', delay: '2s' },
                    { icon: <MdReportProblem size={16} />, top: '42%', right: '3%', size: 40, anim: 'floatA', dur: '5.8s', delay: '0.9s' },
                ].map((f, i) => (
                    <div key={i} className="hero-float" style={{
                        top: f.top, left: f.left, right: f.right, width: f.size, height: f.size,
                        animation: `${f.anim} ${f.dur} ease-in-out infinite`, animationDelay: f.delay,
                    }}>
                        <div className="hero-float-badge">{f.icon}</div>
                    </div>
                ))}

                <div className="public-booking-hero-content" style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto' }}>
                    {(heroTitle || heroSubtitle) && <h1 style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(32px,5vw,54px)', letterSpacing: '-0.02em', lineHeight: 1.15, marginBottom: 22 }}>
                        {heroTitle}{heroTitle && heroSubtitle && <br />}
                        {heroSubtitle && <span style={{ background: 'linear-gradient(135deg,#67e8f9,#a5f3fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                            {heroSubtitle}
                        </span>}
                    </h1>}
                    {heroDescription && <p style={{ color: '#94a3b8', fontSize: 17, maxWidth: 540, margin: '0 auto 40px', lineHeight: 1.7 }}>
                        {heroDescription.replace('{currency}', homepage?.currency || 'RWF')}
                    </p>}
                    {(homepage?.slides || []).length > 1 && <div style={{ display: 'flex', justifyContent: 'center', gap: 7, marginTop: 28 }}>{homepage.slides.map((slide, index) => <button key={index} aria-label={t('publicBooking.showSlideAria', 'Show slide {n}', { n: index + 1 })} onClick={() => setSlideIndex(index)} style={{ width: index === slideIndex ? 28 : 9, height: 9, border: 0, borderRadius: 10, background: index === slideIndex ? '#67e8f9' : 'rgba(255,255,255,.5)', transition: 'all .25s' }} />)}</div>}
                </div>
            </section>

            {!ownSystem && (<>
            {/* ── A Look Inside ── */}
            <section style={{ padding: '84px 5% 80px', background: 'linear-gradient(160deg,#050a18 0%,#0a1330 55%,#0d1b3a 100%)', position: 'relative', overflow: 'hidden' }}>
                <style>{`
                    @keyframes peekSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    @keyframes peekSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
                    @keyframes peekGlow {
                        0%, 100% { box-shadow: 0 0 16px 2px rgba(56,189,248,0.3), inset 0 0 10px rgba(56,189,248,0.12); }
                        50% { box-shadow: 0 0 28px 6px rgba(56,189,248,0.55), inset 0 0 14px rgba(56,189,248,0.26); }
                    }
                    .peek-ring {
                        position: absolute; inset: -10px; border-radius: 50%; pointer-events: none;
                        background: conic-gradient(from 0deg, rgba(56,189,248,0) 0%, rgba(103,232,249,0.95) 12%, rgba(56,189,248,0) 28%, rgba(56,189,248,0) 100%);
                        animation: peekSpin 5s linear infinite;
                    }
                    .peek-orbit { position: absolute; inset: -20px; pointer-events: none; animation: peekSpinRev 6.5s linear infinite; }
                    .peek-orbit span {
                        position: absolute; top: 0; left: 50%; width: 6px; height: 6px; margin-left: -3px;
                        border-radius: 50%; background: #67e8f9; box-shadow: 0 0 8px 2px rgba(103,232,249,0.85);
                    }
                    .peek-icon-core { animation: peekGlow 3s ease-in-out infinite; }
                `}</style>

                {/* faint starfield texture and ambient glow — purely decorative */}
                <div aria-hidden="true" style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.5,
                    backgroundImage: 'radial-gradient(rgba(148,197,255,0.35) 1px, transparent 1px)',
                    backgroundSize: '34px 34px',
                }} />
                <div aria-hidden="true" style={{ position: 'absolute', top: '-12%', left: '8%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.35), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none' }} />
                <div aria-hidden="true" style={{ position: 'absolute', bottom: '-16%', right: '6%', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.28), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none' }} />

                <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <div style={{ textAlign: 'center', marginBottom: 56 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#67e8f9', textTransform: 'uppercase', letterSpacing: '3px', textShadow: '0 0 12px rgba(103,232,249,0.6)' }}>{t('publicBooking.lookInsideLabel', 'A Look Inside')}</span>
                        <h3 style={{ fontWeight: 800, fontSize: 28, color: '#fff', marginTop: 12, marginBottom: 0, letterSpacing: '-0.01em' }}>{t('publicBooking.lookInsideHeading', 'Everything your facility needs, working together')}</h3>
                    </div>
                    <div className="row g-4">
                        {[
                            { icon: <MdChecklist size={28} />, title: t('publicBooking.featureWorkOrdersTitle', 'Work Orders'), desc: t('publicBooking.featureWorkOrdersDesc', 'Submit and track maintenance requests in real time.'), delay: '0s' },
                            { icon: <MdEventAvailable size={28} />, title: t('publicBooking.featureBookingsTitle', 'Bookings'), desc: t('publicBooking.featureBookingsDesc', 'Reserve spaces and manage schedules with ease.'), delay: '0.4s' },
                            { icon: <MdBuild size={28} />, title: t('publicBooking.featureMaintenanceTitle', 'Maintenance'), desc: t('publicBooking.featureMaintenanceDesc', 'Preventive care keeps every facility running smoothly.'), delay: '0.8s' },
                            { icon: <MdInsights size={28} />, title: t('publicBooking.featureAnalyticsTitle', 'Analytics'), desc: t('publicBooking.featureAnalyticsDesc', 'Clear insight into usage, costs, and performance.'), delay: '1.2s' },
                        ].map(({ icon, title, desc, delay }) => (
                            <div className="col-6 col-lg-3" key={title}>
                                <div style={{ textAlign: 'center', padding: '8px 12px' }}>
                                    <div style={{ position: 'relative', width: 76, height: 76, margin: '0 auto 22px' }}>
                                        <div className="peek-ring" />
                                        <div className="peek-orbit"><span /></div>
                                        <div className="peek-icon-core" style={{
                                            position: 'absolute', inset: 8, borderRadius: '50%',
                                            background: 'radial-gradient(circle at 35% 30%, rgba(56,189,248,0.25), rgba(10,19,48,0.92))',
                                            border: '1px solid rgba(103,232,249,0.4)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#67e8f9',
                                            animationDelay: delay,
                                        }}>
                                            {icon}
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 6 }}>{title}</div>
                                    <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            </>)}

            {/* ── Partners ── */}
            {homepage?.partners?.length > 0 && (
                <section id="partners" style={{ padding: '64px 5%', background: '#0a1330', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: 36 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#67e8f9', textTransform: 'uppercase', letterSpacing: '2px' }}>{t('publicBooking.trustedByLabel', 'Trusted By')}</span>
                            <h3 style={{ fontWeight: 800, fontSize: 26, color: '#fff', marginTop: 10, marginBottom: 0, letterSpacing: '-0.01em' }}>{t('publicBooking.ourPartnersHeading', 'Our Partners')}</h3>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 40 }}>
                            {homepage.partners.map((partner, i) => (
                                <img key={i} src={partner.logo} alt={partner.name || 'Partner logo'} title={partner.name || ''}
                                    style={{ height: 48, maxWidth: 160, objectFit: 'contain', filter: 'grayscale(100%)', opacity: 0.65, transition: 'filter 0.2s, opacity 0.2s' }}
                                    onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0%)'; e.currentTarget.style.opacity = '1'; }}
                                    onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(100%)'; e.currentTarget.style.opacity = '0.65'; }} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Footer ── */}
            <footer style={{ background: '#080e22', color: '#94a3b8', padding: '56px 5% 24px' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div className="row g-4 mb-4">
                        <div className="col-lg-4">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                <img src={homepage?.logo_url || '/logo512.png'} alt={`${homepage?.system_name || 'MITI FMS'} logo`} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>{homepage?.system_name || 'MITI FMS'}</div>
                                    {!ownSystem && <div style={{ fontSize: 10, color: '#475569', letterSpacing: '1px' }}>FACILITY MANAGEMENT</div>}
                                </div>
                            </div>
                            {!ownSystem && (
                                <p style={{ fontSize: 14, lineHeight: 1.7, color: '#64748b', maxWidth: 280 }}>
                               {t('publicBooking.footerBlurb', 'MITI facility management platform serving businesses across Rwanda. Book spaces, manage assets, and streamline operations.')}
                            </p>)}
                        </div>
                        <div className="col-lg-2 col-6">
                            <div style={{ fontWeight: 700, color: '#fff', marginBottom: 18, fontSize: 12, textTransform: 'uppercase', letterSpacing: '1px' }}>{t('publicBooking.quickLinksHeading', 'Quick Links')}</div>
                            {[['#home', t('publicBooking.navHome', 'Home')], ['/about', t('publicBooking.navAbout', 'About')], ['/services', t('publicBooking.navServices', 'Services')], ['/booking', t('publicBooking.navBookSpace', 'Book a Space')], ['/report', t('publicBooking.navReport', 'Report')], ['/contact', t('publicBooking.navContact', 'Contact')]].filter(([href]) => !hideLink(href)).map(([href, l]) => (
                                <div key={l} style={{ marginBottom: 10 }}>
                                    <a href={href} style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}
                                        onMouseEnter={e => e.target.style.color = '#67e8f9'} onMouseLeave={e => e.target.style.color = '#64748b'}>
                                        {l}
                                    </a>
                                </div>
                            ))}
                        </div>
                        {!ownSystem && (
                        <div className="col-lg-2 col-6">
                            <div style={{ fontWeight: 700, color: '#fff', marginBottom: 18, fontSize: 12, textTransform: 'uppercase', letterSpacing: '1px' }}>{t('publicBooking.servicesHeading', 'Services')}</div>
                            {[t('publicBooking.svcSpaceBooking', 'Space Booking'), t('publicBooking.svcAssetManagement', 'Asset Management'), t('publicBooking.svcMaintenance', 'Maintenance'), t('publicBooking.svcWorkOrders', 'Work Orders'), t('publicBooking.svcVendorManagement', 'Vendor Management')].map(l => (
                                <div key={l} style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>{l}</span>
                                </div>
                            ))}
                        </div>
                        )}
                        <div className="col-lg-4">
                            <div style={{ fontWeight: 700, color: '#fff', marginBottom: 18, fontSize: 12, textTransform: 'uppercase', letterSpacing: '1px' }}>{t('publicBooking.contactHeading', 'Contact')}</div>
                            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 2 }}>
                                {homepage?.address && <div>📍 {homepage.address}</div>}
                                {homepage?.phone && <div>📞 {homepage.phone}</div>}
                                {homepage?.contact_email && <div>✉️ {homepage.contact_email}</div>}
                                {homepage?.hours && <div>🕐 {homepage.hours}</div>}
                            </div>
                            <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: 12 }}>{t('publicBooking.followUsHeading', 'Follow us')}</div>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    {SOCIAL_LINKS.map(({ key, label, icon }) => {
                                        const href = getSocialUrl(homepage, key).trim();
                                        return <a key={label} href={href || undefined} target={href ? '_blank' : undefined} rel={href ? 'noopener noreferrer' : undefined} aria-disabled={!href} onClick={event => { if (!href) event.preventDefault(); }} aria-label={t('publicBooking.followOnAria', 'Follow {system} on {label}', { system: homepage?.system_name || 'MITI FMS', label })}
                                            title={t('publicBooking.followOnTitle', 'Follow us on {label}', { label })}
                                            style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#94a3b8', textDecoration: 'none', transition: 'background 0.2s, color 0.2s', cursor: href ? 'pointer' : 'default' }}
                                            onMouseEnter={event => { event.currentTarget.style.background = '#2563eb'; event.currentTarget.style.color = '#fff'; }}
                                            onMouseLeave={event => { event.currentTarget.style.background = 'rgba(255,255,255,0.06)'; event.currentTarget.style.color = '#94a3b8'; }}>
                                            {icon}
                                        </a>;
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 22, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                        <span style={{ fontSize: 13 }}>© {new Date().getFullYear()} {homepage?.system_name || 'MITI FMS'}. {t('publicBooking.allRightsReserved', 'All rights reserved.')}</span>
                        <div style={{ display: 'flex', gap: 20 }}>
                            <a href="/privacy-policy" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>{t('publicBooking.privacyPolicyLink', 'Privacy Policy')}</a>
                            <a href="/terms-of-service" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>{t('publicBooking.termsLink', 'Terms of Service')}</a>
                            <a href="/login" style={{ fontSize: 13, color: '#67e8f9', textDecoration: 'none' }}>{t('publicBooking.loginLink', 'Login')}</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
